import { useMemo } from 'react';

import { InternalTData, TableProps } from '@/components/table/types';

interface UseIndexedRowsOptions<TData> {
  tableName: string;
  groupBy: TableProps<TData>['groupBy'];
  expandedGroups: string[];
  status: TableProps<TData>['status'];
}

interface UseIndexedRowsResult<TData> {
  indexedData: InternalTData<TData>[];
  groupedData: InternalTData<TData>[];
}

/**
 * Index raw table rows for React virtualization and apply groupBy filtering.
 *
 * Extracted verbatim from Table.tsx so both Table and the canonical TanStack
 * table can share one implementation (CHARIOT-1377 Bug 1). Behavior is
 * byte-identical to the inline blocks it replaces.
 *
 * Indexing strategy:
 * 1. Use item.key if unique within dataset
 * 2. For duplicates, append occurrence counter for deterministic uniqueness
 * 3. Fallback to index for items without keys
 *
 * Why Key-Based Selection:
 * Selection system uses _key to track selected rows. Unstable keys break
 * selection when data is re-sorted or filtered.
 *
 * Known Issue:
 * Backend may return duplicate keys for nuclei template vulnerabilities.
 * This is a workaround until backend ensures unique identifiers.
 *
 * @see selection.value - stores selected row._key values
 * @see handleRowClick - uses _key for selection toggling
 */
export function useIndexedRows<TData>(
  rawData: TableProps<TData>['data'],
  { tableName, groupBy, expandedGroups, status }: UseIndexedRowsOptions<TData>
): UseIndexedRowsResult<TData> {
  const indexedData: InternalTData<TData>[] = useMemo(() => {
    const keyOccurrences = new Map<string, number>();

    return rawData.map((item, _index) => {
      const itemKey = (item as Record<string, unknown>)?.key;
      const keyValue = typeof itemKey === 'string' ? itemKey : undefined;

      let generatedKey: string;

      if (keyValue) {
        // Track occurrence count for this key
        const occurrenceIndex = keyOccurrences.get(keyValue) ?? 0;
        keyOccurrences.set(keyValue, occurrenceIndex + 1);

        // Append occurrence index only for duplicates
        if (occurrenceIndex > 0) {
          generatedKey = `${keyValue}__dup${occurrenceIndex}`;

          // Warn developers in development mode only
          console.warn(
            `Table "${tableName}": Duplicate key detected - "${keyValue}" ` +
              `(occurrence ${occurrenceIndex + 1}). ` +
              `This may indicate a backend data quality issue.`,
            { rowIndex: _index, item }
          );
        } else {
          generatedKey = keyValue;
        }
      } else {
        // Fallback to index for items without keys
        generatedKey = `__row${_index}`;
      }

      return {
        ...item,
        _idx: _index.toString(),
        _key: generatedKey,
      } as InternalTData<TData>;
    });
  }, [rawData, tableName]);

  // Filter the data based on the groupBy value
  const groupedData = useMemo(() => {
    const groupedData =
      groupBy && status === 'success'
        ? groupBy.reduce((acc, group) => {
            const filteredData = indexedData.filter(item => group.filter(item));
            // Add the group label only when there is filtered data available
            const newData =
              filteredData.length > 0
                ? [
                    ...acc,
                    {
                      _label: group.label,
                      _type: 'colgroup',
                      _idx: -1,
                      _icon: group.icon,
                    },
                    ...(expandedGroups.includes(group.label)
                      ? filteredData
                      : []),
                  ]
                : acc;

            return newData as InternalTData<TData>[];
          }, [] as InternalTData<TData>[])
        : indexedData;

    return groupedData;
  }, [groupBy, indexedData, expandedGroups, status]);

  return { indexedData, groupedData };
}
